import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowUpRight,
  CheckCircle2,
  ExternalLink,
  FileText,
  Inbox,
  Eye,
  MessageSquareReply,
  Send,
  X,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { inspectionsAPI } from "../../../services/api";

const norm = (value) => (value || "").toString().toLowerCase();
const titleCase = (value) =>
  (value || "-")
    .toString()
    .replaceAll("_", " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const reviewDecisionLabel = (value) => {
  const s = norm(value);
  if (["approved", "reviewed", "ready_for_closeout"].includes(s)) return "Approved / Closed";
  if (s === "needs_further_action") return "Needs Further Action";
  return value ? titleCase(value) : "Not reviewed";
};

function ResponseModal({ item, loading, onClose, onSubmit }) {
  const [text, setText] = useState("");
  useEffect(() => setText(""), [item]);
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Respond to escalation</p>
            <h3 className="mt-1 font-semibold text-gray-900">{item.finding_number || `Issue #${item.finding_id}`}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 p-4">
          <div className="rounded-xl border bg-gray-50 p-3 text-sm text-gray-700">
            <p className="font-medium text-gray-900">Why it was escalated</p>
            <p className="mt-1 whitespace-pre-wrap">{item.reason}</p>
          </div>
          <textarea
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write your response, direction, or resolution guidance..."
            className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <p className="text-xs text-gray-500">
            This records your direction on the issue. The issue remains active until an authorized reviewer selects Approve / Close.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            disabled={loading || !text.trim()}
            onClick={() => onSubmit({ response_text: text.trim() })}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
          >
            {loading ? <LoadingSpinner size="small" /> : <Send className="h-4 w-4" />} Send Response
          </button>
        </div>
      </div>
    </div>
  );
}

function ForwardModal({ item, targets, targetsLoading, loading, onClose, onSubmit }) {
  const [targetUserId, setTargetUserId] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    setTargetUserId("");
    setReason("");
  }, [item]);
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Escalate further</p>
            <h3 className="mt-1 font-semibold text-gray-900">{item.finding_number || `Issue #${item.finding_id}`}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4 p-4">
          {targetsLoading ? (
            <div className="flex justify-center p-6"><LoadingSpinner size="small" /></div>
          ) : targets.length === 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              No higher target is available in the published organization chart.
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Escalate To *</span>
                <select value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                  <option value="">Select person</option>
                  {targets.map((target) => (
                    <option key={target.user_id} value={target.user_id}>{target.name} — {titleCase(target.role)}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Reason *</span>
                <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3" placeholder="Explain why higher-level input is required..." />
              </label>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            disabled={loading || targetsLoading || !targetUserId || !reason.trim()}
            onClick={() => onSubmit({ escalated_to_user_id: Number(targetUserId), reason: reason.trim() })}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
          >
            {loading ? <LoadingSpinner size="small" /> : <ArrowUpRight className="h-4 w-4" />} Escalate
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EscalationInboxPanel({ projectId, projectRole, items = [], onRefresh, onError, onSuccess }) {
  const [responseItem, setResponseItem] = useState(null);
  const [forwardItem, setForwardItem] = useState(null);
  const [targets, setTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contextItemId, setContextItemId] = useState(null);

  const canForward = ["general_foreman", "project_engineer"].includes(norm(projectRole));
  const counts = useMemo(() => ({
    total: items.length,
    open: items.filter((item) => norm(item.status) === "open").length,
    answered: items.filter((item) => norm(item.status) === "answered").length,
    forwarded: items.filter((item) => norm(item.status) === "forwarded").length,
  }), [items]);

  const submitResponse = async (payload) => {
    if (!responseItem) return;
    setSaving(true);
    try {
      await inspectionsAPI.answerFindingEscalation(projectId, responseItem.id, payload);
      setResponseItem(null);
      onSuccess?.("Escalation response recorded. Issue remains active until authorized review.");
      await onRefresh?.();
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Failed to respond to escalation.");
    } finally {
      setSaving(false);
    }
  };

  const openForward = async (item) => {
    setForwardItem(item);
    setTargets([]);
    setTargetsLoading(true);
    try {
      const res = await inspectionsAPI.getFindingEscalationTargets(projectId, item.finding_id);
      setTargets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Failed to load escalation targets.");
    } finally {
      setTargetsLoading(false);
    }
  };

  const submitForward = async (payload) => {
    if (!forwardItem) return;
    setSaving(true);
    try {
      await inspectionsAPI.forwardFindingEscalation(projectId, forwardItem.id, payload);
      setForwardItem(null);
      setTargets([]);
      onSuccess?.("Escalation forwarded to the next reporting level.");
      await onRefresh?.();
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Failed to escalate further.");
    } finally {
      setSaving(false);
    }
  };

  const openFile = async (_item, file) => {
    if (!file?.id) return;
    const previewWindow = window.open("", "_blank");
    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.title = file.name || "Opening file";
      previewWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:24px;color:#374151">Opening file…</div>';
    }
    try {
      const res = await inspectionsAPI.getDocumentDownloadUrl(projectId, file.id);
      const freshUrl = res?.data?.url;
      if (!freshUrl) throw new Error("A fresh file link could not be generated.");
      if (previewWindow && !previewWindow.closed) previewWindow.location.replace(freshUrl);
      else window.location.assign(freshUrl);
    } catch (err) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      onError?.(err?.response?.data?.detail || err?.message || "Failed to open file.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-2xl border bg-white p-4"><p className="text-xs uppercase text-gray-500">Escalated to me</p><p className="mt-2 text-3xl font-bold">{counts.total}</p></div>
        <div className="rounded-2xl border bg-white p-4"><p className="text-xs uppercase text-gray-500">Awaiting response</p><p className="mt-2 text-3xl font-bold text-amber-700">{counts.open}</p></div>
        <div className="rounded-2xl border bg-white p-4"><p className="text-xs uppercase text-gray-500">Answered</p><p className="mt-2 text-3xl font-bold text-emerald-700">{counts.answered}</p></div>
        <div className="rounded-2xl border bg-white p-4"><p className="text-xs uppercase text-gray-500">Escalated further</p><p className="mt-2 text-3xl font-bold text-blue-700">{counts.forwarded}</p></div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-gray-500">
          <Inbox className="mx-auto mb-2 h-7 w-7 text-gray-400" /> No issues are escalated directly to you.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const contextOpen = Number(contextItemId) === Number(item.id);
            return (
              <div key={item.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 p-3 lg:flex-row lg:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {item.finding_number || `Issue #${item.finding_id}`}
                      </span>
                      {item.inspection_number ? (
                        <span className="text-xs text-gray-500">INSP {item.inspection_number}</span>
                      ) : null}
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                        {titleCase(item.status)}
                      </span>
                      {item.is_automatic ? (
                        <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-700">Automatic</span>
                      ) : null}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span>Responsible: {item.responsible_user_name || "—"}</span>
                      <span>From: {item.escalated_by_name || "—"} ({titleCase(item.from_role)})</span>
                      <span>Severity: {item.severity || "—"}</span>
                      <span>Due: {item.due_date || "—"}</span>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setContextItemId(contextOpen ? null : item.id)}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      <Eye className="h-4 w-4" /> {contextOpen ? "Hide Context" : "Context"}
                    </button>
                    {["open", "forwarded"].includes(norm(item.status)) ? (
                      <button type="button" onClick={() => setResponseItem(item)} className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600">
                        <MessageSquareReply className="h-4 w-4" /> {norm(item.status) === "forwarded" ? "Respond Back" : "Respond"}
                      </button>
                    ) : null}
                    {canForward && norm(item.status) === "open" ? (
                      <button type="button" onClick={() => openForward(item)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                        <ArrowUpRight className="h-4 w-4" /> Escalate Further
                      </button>
                    ) : null}
                  </div>
                </div>

                {contextOpen ? (
                  <div className="border-t bg-gray-50/70 p-4">
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Issue context</p>
                          <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{item.description || "No description."}</p>
                        </div>
                        {item.corrective_action ? (
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Corrective action</p>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{item.corrective_action}</p>
                          </div>
                        ) : null}
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <span>Location: {item.location || "—"}</span>
                          <span>Assigned by: {item.assigned_by_name || "—"}</span>
                          <span>Field action: {item.foreman_action || "—"}</span>
                          <span>Responded: {item.responded_at ? new Date(item.responded_at).toLocaleString() : "—"}</span>
                        </div>
                        {item.response_notes ? (
                          <div className="rounded-xl border bg-white p-3 text-sm">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Field response</p>
                            <p className="mt-1 whitespace-pre-wrap text-gray-800">{item.response_notes}</p>
                          </div>
                        ) : null}
                        {Array.isArray(item.history) && item.history.length ? (
                          <div className="rounded-xl border bg-white p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Issue history</p>
                            <div className="mt-2 max-h-52 space-y-2 overflow-y-auto">
                              {[...item.history].reverse().map((entry) => (
                                <div key={`${entry.id}-${entry.created_at || ""}`} className="rounded-lg bg-gray-50 px-2.5 py-2 text-xs text-gray-700">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="font-semibold text-gray-900">
                                      {entry.user_name || "Team member"}
                                      {entry.user_role ? ` • ${titleCase(entry.user_role)}` : ""}
                                    </span>
                                    <span className="text-gray-400">
                                      {entry.created_at ? new Date(entry.created_at).toLocaleString() : ""}
                                    </span>
                                  </div>
                                  <p className="mt-1 whitespace-pre-wrap">{entry.body}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>

                      <div className="space-y-3">
                        <div className="rounded-xl border border-orange-100 bg-orange-50 p-3 text-sm">
                          <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Escalation reason</p>
                          <p className="mt-1 whitespace-pre-wrap text-gray-800">{item.reason}</p>
                        </div>
                        {item.review_status ? (
                          <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                            <p className="font-semibold">Latest review: {reviewDecisionLabel(item.review_status)}</p>
                            <p className="mt-1 text-xs">{item.reviewed_by_name || "—"} • {titleCase(item.reviewer_role)}</p>
                            {item.review_comments ? <p className="mt-1 whitespace-pre-wrap">{item.review_comments}</p> : null}
                          </div>
                        ) : null}
                        {item.upstream_response_text ? (
                          <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
                            <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Upstream Response</div>
                            <p className="mt-1 whitespace-pre-wrap">{item.upstream_response_text}</p>
                          </div>
                        ) : null}
                        {item.response_text ? (
                          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-800">
                            <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Escalation Response</div>
                            <p className="mt-1 whitespace-pre-wrap">{item.response_text}</p>
                          </div>
                        ) : null}
                        <div className="rounded-xl border bg-white p-3">
                          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900"><FileText className="h-4 w-4 text-orange-600" /> Related Files</div>
                          {item.files?.length ? (
                            <div className="max-h-40 space-y-1.5 overflow-y-auto">
                              {item.files.map((file) => (
                                <button key={file.id} type="button" onClick={() => openFile(item, file)} className="flex w-full items-center justify-between gap-3 rounded-lg border bg-gray-50 px-3 py-2 text-left text-sm hover:border-orange-200 hover:bg-orange-50">
                                  <span className="truncate">{file.name || `File #${file.id}`}</span>
                                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-orange-700" />
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">No related files. {item.inspection_pdf_available ? "Inspection PDF is available." : ""}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <ResponseModal
        item={responseItem}
        loading={saving}
        onClose={() => setResponseItem(null)}
        onSubmit={submitResponse}
      />
      <ForwardModal item={forwardItem} targets={targets} targetsLoading={targetsLoading} loading={saving} onClose={() => setForwardItem(null)} onSubmit={submitForward} />
    </div>
  );
}
